import { LoadingOutlined } from '@ant-design/icons'
import { ChatParticipant, OrganizationMember } from '@prisma/client'
import { useNavigate } from '@remix-run/react'
import { Col, Flex, Row, Spin } from 'antd'
import { useMemo } from 'react'
import { OrganizationMemberFromContext } from '~/core/context/organization/hooks/useOrganizationMembers'
import { OrganizationType } from '~/features/organization/common'
import { useDriveFiles } from '../../hooks/useDriveFiles'
import { PlaceholderEmpty } from '../PlaceholderEmpty'
import { FileCard } from './components/FileCard'

type Props = {
  organization: OrganizationType
  organizationMember: OrganizationMember
  agent: OrganizationMemberFromContext
  organizationMembers: OrganizationMemberFromContext[]
  participants: ChatParticipant[]
  conversationId: string
}

export const DriveFileListCards: React.FC<Props> = ({
  organization,
  organizationMember,
  agent,
  organizationMembers,
  participants,
  conversationId,
}) => {
  const navigate = useNavigate()

  const findAgentOrganizationMember = () => {
    if (!participants) {
      return
    }

    const organizationMembersParticipantIds = participants.map(
      participant => participant.organizationMemberId,
    )

    const organizationMembersParticipants = organizationMembers.filter(
      organizationMember =>
        organizationMembersParticipantIds.includes(organizationMember.id),
    )

    return organizationMembersParticipants.find(
      organizationMember => organizationMember.role === 'AGENT',
    )
  }

  const organizationMemberAgent = useMemo(() => {
    return findAgentOrganizationMember()
  }, [participants, organizationMembers])

  const { driveFiles, isLoading, updateDriveFile, deleteDriveFile, refetch } =
    useDriveFiles({
      organization,
      organizationMember: organizationMemberAgent,
    })

  const isEmpty = driveFiles.length === 0 && !isLoading

  const handleClick = (driveFileId: string) => {
    navigate(
      `/organizations/${organization.id}/chat/${conversationId}/blog-posts/${driveFileId}`,
    )
  }

  const handlePublished = async (driveFileId: string, platform: string) => {
    const driveFileSelected = driveFiles.find(
      driveFile => driveFile.id === driveFileId,
    )

    const driveFileUpdated = {
      driveFileId,
      values: {
        ...(driveFileSelected?.values as any),
        status: 'PUBLISHED',
        platform,
      },
    }

    await updateDriveFile(driveFileUpdated)
  }

  return (
    <>
      {isEmpty ? (
        <>
          <Flex vertical className="w-full h-full">
            <PlaceholderEmpty />
          </Flex>
        </>
      ) : (
        <Flex
          vertical
          className="w-full h-full p-5 pb-28"
          style={{ overflowY: 'auto' }}
        >
          <Row gutter={[24, 24]}>
            {driveFiles?.map(driveFile => (
              <Col xs={24} sm={12} md={8} key={driveFile.id}>
                <FileCard
                  agent={agent}
                  organizationMember={organizationMember}
                  file={driveFile}
                  onSelect={handleClick}
                  onDelete={deleteDriveFile}
                  onPublish={handlePublished}
                />
              </Col>
            ))}
          </Row>

          {isLoading && (
            <Spin
              indicator={<LoadingOutlined spin />}
              style={{ margin: '10px auto', display: 'block' }}
            />
          )}
        </Flex>
      )}
    </>
  )
}
